import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import {
  getCardgames,
  getGameIcon,
  listUserDecks,
  fetchUserTournamentReports,
  getStoredReports,
  saveUserTournamentReport,
  deleteUserTournamentReport,
  UserTournamentReport,
  UserTournamentReportRound,
  EVENT_CATEGORIES,
  getTournamentEventCategories,
  TOURNAMENT_FORMATS,
  getTournamentFormatsForReports,
  TOURNAMENT_TYPES,
  getTournamentTypes,
  getGameSetsForReports,
  TournamentMetaDeck,
  getTournamentMetaDecks,
  DEFAULT_META_DECKS
} from '../../services/supabaseService';
import { getGameNameById } from '../../constants/deckRules';
import { GameSelector } from '../../components/GameSelector';

interface ReportsProps {
  activeGame?: number | 'All';
  setActiveGame?: (game: number | 'All') => void;
}

export const Reports: React.FC<ReportsProps> = ({ activeGame = 'All', setActiveGame }) => {
  const { user } = useAuth();
  const { showNotification } = useNotification();

  // State: Reports list
  const [reports, setReports] = useState<UserTournamentReport[]>([]);
  const [isLoadingReports, setIsLoadingReports] = useState(true);
  const [dbGames, setDbGames] = useState<{ id: any; name: string; slug?: string }[]>([]);
  const [eventCategories, setEventCategories] = useState(EVENT_CATEGORIES);
  const [tournamentFormats, setTournamentFormats] = useState(TOURNAMENT_FORMATS);
  const [tournamentTypes, setTournamentTypes] = useState(TOURNAMENT_TYPES);
  const [gameSets, setGameSets] = useState<any[]>([]);
  const [userDecksList, setUserDecksList] = useState<any[]>([]);
  const [tournamentMetaDecks, setTournamentMetaDecks] = useState<TournamentMetaDeck[]>([]);
  const [expandedMetaRounds, setExpandedMetaRounds] = useState<Record<number, boolean>>({});

  // Expanded rounds in list view
  const [expandedRounds, setExpandedRounds] = useState<Record<string, boolean>>({});

  // Filter and Navigation State
  const [activeTab, setActiveTab] = useState<'all' | 'my-reports'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStandingFilter, setSelectedStandingFilter] = useState<'all' | 'champion' | 'topcut' | 'positive'>('all');
  const [selectedGameFilter, setSelectedGameFilter] = useState<number>(() => {
    if (activeGame && activeGame !== 'All') return Number(activeGame);
    return 10;
  });
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string | 'All'>('All');
  const [filterGameSets, setFilterGameSets] = useState<any[]>([]);
  const [selectedSetFilter, setSelectedSetFilter] = useState<string>('All');

  // MODAL STATE: Form direto e focado
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingReportId, setEditingReportId] = useState<string | number | null>(null);

  // DELETE CONFIRMATION MODAL STATE
  const [reportToDelete, setReportToDelete] = useState<UserTournamentReport | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form Fields Obrigatórios
  const [formTournamentName, setFormTournamentName] = useState('');
  const [formEventCategoryId, setFormEventCategoryId] = useState<string>('1');
  const [formPlayedAt, setFormPlayedAt] = useState(new Date().toISOString().split('T')[0]);
  const [formGameId, setFormGameId] = useState<number | string>(10);
  const [formWins, setFormWins] = useState<number>(0);
  const [formLosses, setFormLosses] = useState<number>(0);
  const [formDraws, setFormDraws] = useState<number>(0);

  // Novos campos: Formato, Tipo de Torneio e Set do Jogo
  const [formFormatId, setFormFormatId] = useState<string>('');
  const [formTournamentTypeId, setFormTournamentTypeId] = useState<string>('1');
  const [formTourneySetId, setFormTourneySetId] = useState<string>('');

  // Rodadas do Torneio
  const [formRounds, setFormRounds] = useState<UserTournamentReportRound[]>([]);

  // Deck (Opcional)
  const [selectedDeckOption, setSelectedDeckOption] = useState<string>('manual');
  const [formDeckName, setFormDeckName] = useState('');

  // Colocação e Jogadores (Opcionais)
  const [formPlacement, setFormPlacement] = useState<string>('');
  const [formTotalPlayers, setFormTotalPlayers] = useState<string>('');

  // Sincronizar activeGame das props se for válido
  useEffect(() => {
    if (activeGame && activeGame !== 'All') {
      setSelectedGameFilter(Number(activeGame));
    }
  }, [activeGame]);

  // Carregar dados iniciais: Cardgames, Categorias de Evento, Tipos de Torneio e Reports
  useEffect(() => {
    let isMounted = true;

    // Buscar jogos
    getCardgames().then(games => {
      if (isMounted && Array.isArray(games) && games.length > 0) {
        setDbGames(games);
        // Garantir que um jogo específico esteja selecionado no filtro interno
        setSelectedGameFilter(prev => {
          if (prev && games.some(g => Number(g.id) === Number(prev))) {
            return prev;
          }
          if (activeGame && activeGame !== 'All' && games.some(g => Number(g.id) === Number(activeGame))) {
            return Number(activeGame);
          }
          return Number(games[0].id);
        });

        if (!games.some(g => Number(g.id) === Number(formGameId))) {
          setFormGameId(games[0].id);
        }
      }
    });

    // Buscar categorias de eventos
    getTournamentEventCategories().then(cats => {
      if (isMounted && Array.isArray(cats) && cats.length > 0) {
        setEventCategories(cats);
      }
    });

    // Buscar tipos de torneio
    getTournamentTypes().then(types => {
      if (isMounted && Array.isArray(types) && types.length > 0) {
        setTournamentTypes(types);
      }
    });

    // Buscar formatos de torneio
    getTournamentFormatsForReports().then(formats => {
      if (isMounted && Array.isArray(formats) && formats.length > 0) {
        setTournamentFormats(formats);
      }
    });

    // Buscar reports de torneios do banco (public.user_tournament_reports)
    setIsLoadingReports(true);
    fetchUserTournamentReports()
      .then(data => {
        if (isMounted) {
          const map = new Map<string, UserTournamentReport>();
          (data || []).forEach(r => {
            if (r && r.id !== undefined && r.id !== null) {
              map.set(String(r.id), r);
            }
          });
          setReports(Array.from(map.values()));
          setIsLoadingReports(false);
        }
      })
      .catch(err => {
        console.error('Error fetching reports:', err);
        if (isMounted) {
          const map = new Map<string, UserTournamentReport>();
          getStoredReports().forEach(r => {
            if (r && r.id !== undefined && r.id !== null) {
              map.set(String(r.id), r);
            }
          });
          setReports(Array.from(map.values()));
          setIsLoadingReports(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Carregar coleções para o filtro de reports (somente tourney_restrict === true)
  // Por padrão, a primeira coleção é selecionada
  useEffect(() => {
    let isMounted = true;
    if (!selectedGameFilter) return;

    getGameSetsForReports(selectedGameFilter, { tourneyRestrictOnly: true }).then(sets => {
      if (!isMounted) return;
      const validSets = (sets || []).filter(
        (s: any) => s.tourney_restrict === true || s.tourney_restrict === 'true' || s.tourney_restrict === 1
      );
      setFilterGameSets(validSets);

      // Por padrão, a primeira coleção é selecionada
      if (validSets.length > 0) {
        setSelectedSetFilter(String(validSets[0].id));
      } else {
        setSelectedSetFilter('All');
      }
    }).catch(err => {
      console.warn('Erro ao carregar coleções para filtro de reports:', err);
      if (isMounted) {
        setFilterGameSets([]);
        setSelectedSetFilter('All');
      }
    });

    return () => {
      isMounted = false;
    };
  }, [selectedGameFilter]);

  // Carregar meta decks do jogo selecionado para resolução de thumbnails e cores nas rodadas
  useEffect(() => {
    let isMounted = true;
    if (!selectedGameFilter) return;

    getTournamentMetaDecks(selectedGameFilter).then(metaDecks => {
      if (!isMounted || !Array.isArray(metaDecks)) return;
      setTournamentMetaDecks(prev => {
        const map = new Map<string, TournamentMetaDeck>();
        prev.forEach(d => {
          if (d && d.id !== undefined && d.id !== null) {
            map.set(String(d.id), d);
          }
        });
        metaDecks.forEach(d => {
          if (d && d.id !== undefined && d.id !== null) {
            map.set(String(d.id), d);
          }
        });
        return Array.from(map.values());
      });
    }).catch(err => {
      console.warn('Erro ao carregar meta decks do jogo:', err);
    });

    return () => {
      isMounted = false;
    };
  }, [selectedGameFilter]);

  // Carregar sets do jogo (somente tourney_restrict === true), formatos e decks do usuário quando o jogo selecionado mudar
  useEffect(() => {
    if (formGameId) {
      getGameSetsForReports(formGameId, { tourneyRestrictOnly: true }).then(sets => {
        const loadedSets = (sets || []).filter(
          (s: any) => s.tourney_restrict === true || s.tourney_restrict === 'true' || s.tourney_restrict === 1
        );
        setGameSets(loadedSets);
        // Resetar o set selecionado se ele não pertencer aos sets válidos do jogo atual
        setFormTourneySetId(prev => {
          if (!prev) return '';
          const existsInNewGame = loadedSets.some(s => String(s.id) === String(prev));
          return existsInNewGame ? prev : '';
        });
      });

      getTournamentFormatsForReports(formGameId).then(formats => {
        const loadedFormats = formats || [];
        setTournamentFormats(loadedFormats);
        setFormFormatId(prev => {
          if (!prev) return '';
          const exists = loadedFormats.some(f => String(f.id) === String(prev));
          return exists ? prev : '';
        });
      });

      getTournamentMetaDecks(formGameId, formFormatId || undefined).then(metaDecks => {
        const map = new Map<string, TournamentMetaDeck>();
        (metaDecks || []).forEach(d => {
          if (d && d.id !== undefined && d.id !== null) {
            map.set(String(d.id), d);
          }
        });
        setTournamentMetaDecks(Array.from(map.values()));
      });
    } else {
      setGameSets([]);
      setFormTourneySetId('');
      setTournamentMetaDecks([]);
    }

    if (user?.id) {
      listUserDecks(String(formGameId)).then(decks => {
        setUserDecksList(decks || []);
      });
    }
  }, [user?.id, formGameId, formFormatId]);

  const getColorBadgeStyle = (color: string) => {
    const c = color.toLowerCase();
    if (c.includes('red') || c.includes('vermelho') || c.includes('aggression') || c.includes('fire')) {
      return 'bg-red-500/20 text-red-300 border-red-500/40';
    }
    if (c.includes('blue') || c.includes('azul') || c.includes('vigilance') || c.includes('water')) {
      return 'bg-blue-500/20 text-blue-300 border-blue-500/40';
    }
    if (c.includes('green') || c.includes('verde') || c.includes('command') || c.includes('grass')) {
      return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40';
    }
    if (c.includes('yellow') || c.includes('amarelo') || c.includes('cunning') || c.includes('lightning')) {
      return 'bg-amber-500/20 text-amber-300 border-amber-500/40';
    }
    if (c.includes('purple') || c.includes('roxo') || c.includes('psychic') || c.includes('amethyst')) {
      return 'bg-purple-500/20 text-purple-300 border-purple-500/40';
    }
    if (c.includes('black') || c.includes('preto') || c.includes('villainy') || c.includes('dark')) {
      return 'bg-slate-700/50 text-slate-300 border-slate-600/50';
    }
    if (c.includes('white') || c.includes('branco') || c.includes('heroism') || c.includes('light')) {
      return 'bg-amber-100/20 text-amber-200 border-amber-200/40';
    }
    return 'bg-slate-800 text-slate-300 border-slate-700';
  };

  const handleSelectMetaDeck = (roundIndex: number, metaDeck: TournamentMetaDeck | null) => {
    if (metaDeck) {
      handleUpdateRound(roundIndex, {
        opponent_deck: metaDeck.display_name || metaDeck.name,
        opponent_meta_deck_id: metaDeck.id,
        opponent_meta_deck: metaDeck,
        opponent_deck_image_url: metaDeck.image_url || null
      });
    } else {
      handleUpdateRound(roundIndex, {
        opponent_deck: '',
        opponent_meta_deck_id: null,
        opponent_meta_deck: null,
        opponent_deck_image_url: null
      });
    }
  };

  const handleOpponentDeckInputChange = (roundIndex: number, typedValue: string) => {
    const trimmed = typedValue.trim().toLowerCase();
    const match = tournamentMetaDecks.find(
      m => m.name.toLowerCase() === trimmed ||
           (m.display_name && m.display_name.toLowerCase() === trimmed) ||
           (m.slug && m.slug.toLowerCase() === trimmed)
    );

    if (match) {
      handleUpdateRound(roundIndex, {
        opponent_deck: typedValue,
        opponent_meta_deck_id: match.id,
        opponent_meta_deck: match,
        opponent_deck_image_url: match.image_url || null
      });
    } else {
      handleUpdateRound(roundIndex, {
        opponent_deck: typedValue,
        opponent_meta_deck_id: null,
        opponent_meta_deck: null,
        opponent_deck_image_url: null
      });
    }
  };

  const toggleMetaRounds = (roundIndex: number) => {
    setExpandedMetaRounds(prev => ({
      ...prev,
      [roundIndex]: !prev[roundIndex]
    }));
  };

  // Recalcular W-L-D baseado nas rodadas
  const syncTotalsFromRounds = (roundsList: UserTournamentReportRound[]) => {
    let w = 0, l = 0, d = 0;
    roundsList.forEach(r => {
      if (r.result === 'win') w++;
      else if (r.result === 'loss') l++;
      else if (r.result === 'draw') d++;
    });
    setFormWins(w);
    setFormLosses(l);
    setFormDraws(d);
  };

  // Resolver thumbnail do deck para exibição no Histórico de Partidas por Rodada
  const getRoundDeckImage = (round: UserTournamentReportRound): string | null => {
    if (round.opponent_deck_image_url) return round.opponent_deck_image_url;
    if (round.opponent_meta_deck?.image_url) return round.opponent_meta_deck.image_url;

    // Buscar em tournamentMetaDecks pelo id
    if (round.opponent_meta_deck_id) {
      const meta = tournamentMetaDecks.find(m => String(m.id) === String(round.opponent_meta_deck_id));
      if (meta?.image_url) return meta.image_url;
      const defMeta = DEFAULT_META_DECKS.find(m => String(m.id) === String(round.opponent_meta_deck_id));
      if (defMeta?.image_url) return defMeta.image_url;
    }

    // Buscar pelo nome do deck
    if (round.opponent_deck) {
      const name = round.opponent_deck.toLowerCase().trim();
      const metaByName = tournamentMetaDecks.find(m =>
        m.name.toLowerCase().trim() === name ||
        (m.display_name && m.display_name.toLowerCase().trim() === name) ||
        (m.slug && m.slug.toLowerCase().trim() === name) ||
        name.includes(m.name.toLowerCase().trim())
      );
      if (metaByName?.image_url) return metaByName.image_url;

      const defByName = DEFAULT_META_DECKS.find(m =>
        m.name.toLowerCase().trim() === name ||
        (m.display_name && m.display_name.toLowerCase().trim() === name) ||
        (m.slug && m.slug.toLowerCase().trim() === name) ||
        name.includes(m.name.toLowerCase().trim())
      );
      if (defByName?.image_url) return defByName.image_url;
    }

    return null;
  };

  // Funções de gerenciamento de rodadas no formulário
  const handleAddRound = () => {
    const nextRoundNumber = formRounds.length + 1;
    const newRound: UserTournamentReportRound = {
      round_number: nextRoundNumber,
      opponent_name: '',
      opponent_deck: '',
      won_dice: null,
      play_order: null,
      result: 'win'
    };
    const updated = [...formRounds, newRound];
    setFormRounds(updated);
    syncTotalsFromRounds(updated);
  };

  const handleUpdateRound = (index: number, updates: Partial<UserTournamentReportRound>) => {
    setFormRounds(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...updates };
      if (updates.result !== undefined) {
        syncTotalsFromRounds(updated);
      }
      return updated;
    });
  };

  const handleRemoveRound = (index: number) => {
    const updated = formRounds
      .filter((_, idx) => idx !== index)
      .map((r, idx) => ({ ...r, round_number: idx + 1 }));
    setFormRounds(updated);
    syncTotalsFromRounds(updated);
  };

  // Verificar se o usuário autenticado é o dono do report
  const isReportOwner = (rep?: UserTournamentReport | null) => {
    if (!user || !rep) return false;
    const currentUserId = String(user.id);
    const repUserId = String(rep.user_id);
    if (repUserId && repUserId === currentUserId) return true;
    if (user.auth_id && String(user.auth_id) === repUserId) return true;
    return false;
  };

  // Abrir modal de criação ou edição
  const handleOpenModal = (reportToEdit?: UserTournamentReport) => {
    if (!user?.id) {
      showNotification('Você precisa estar conectado para registrar ou editar reports de torneios.', 'error');
      return;
    }

    if (reportToEdit) {
      if (!isReportOwner(reportToEdit) && user?.role !== 'admin') {
        showNotification('Você só tem permissão para editar os seus próprios reports.', 'error');
        return;
      }

      setEditingReportId(reportToEdit.id);
      setFormTournamentName(reportToEdit.name || '');
      setFormEventCategoryId(reportToEdit.event_category_id ? String(reportToEdit.event_category_id) : '1');
      setFormFormatId(reportToEdit.format_id ? String(reportToEdit.format_id) : '');
      setFormTournamentTypeId(reportToEdit.tournament_type_id ? String(reportToEdit.tournament_type_id) : '1');
      setFormTourneySetId(reportToEdit.tourney_set_id ? String(reportToEdit.tourney_set_id) : '');
      setFormPlayedAt(reportToEdit.played_at ? reportToEdit.played_at.substring(0, 10) : new Date().toISOString().split('T')[0]);
      setFormGameId(reportToEdit.game_id || (dbGames[0]?.id || 10));
      setFormWins(Number(reportToEdit.wins) || 0);
      setFormLosses(Number(reportToEdit.losses) || 0);
      setFormDraws(Number(reportToEdit.draws) || 0);

      // Rodadas
      if (Array.isArray(reportToEdit.rounds) && reportToEdit.rounds.length > 0) {
        setFormRounds([...reportToEdit.rounds]);
      } else {
        setFormRounds([]);
      }

      setSelectedDeckOption(reportToEdit.deck_id ? String(reportToEdit.deck_id) : 'manual');
      setFormDeckName(reportToEdit.deck_name || '');
      setFormPlacement(reportToEdit.placement !== null && reportToEdit.placement !== undefined ? String(reportToEdit.placement) : '');
      setFormTotalPlayers(reportToEdit.total_players ? String(reportToEdit.total_players) : '');
    } else {
      setEditingReportId(null);
      setFormTournamentName('');
      setFormEventCategoryId('1');
      setFormFormatId('');
      setFormTournamentTypeId('1');
      setFormTourneySetId('');
      setFormPlayedAt(new Date().toISOString().split('T')[0]);
      setFormGameId(selectedGameFilter || (dbGames[0]?.id || 10));
      setFormWins(0);
      setFormLosses(0);
      setFormDraws(0);
      setFormRounds([]);

      setSelectedDeckOption('manual');
      setFormDeckName('');
      setFormPlacement('');
      setFormTotalPlayers('');
    }
    setIsModalOpen(true);
  };

  // Tratar seleção de deck existente
  const handleDeckOptionChange = (optionVal: string) => {
    setSelectedDeckOption(optionVal);
    if (optionVal !== 'manual') {
      const found = userDecksList.find(d => String(d.id) === String(optionVal));
      if (found) {
        setFormDeckName(found.name || 'Meu Deck');
      }
    }
  };

  // Salvar report diretamente na tabela public.user_tournament_reports
  const handleSaveReport = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formTournamentName.trim()) {
      showNotification('Por favor, informe o nome do torneio.', 'error');
      return;
    }

    if (!formEventCategoryId) {
      showNotification('Por favor, selecione a categoria do evento.', 'error');
      return;
    }

    if (!formPlayedAt) {
      showNotification('Por favor, selecione a data do torneio.', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      // Identificar snapshot do deck caso tenha selecionado um deck do usuário
      let snapshotDeckData: any = null;
      let finalDeckId: string | null = null;

      if (selectedDeckOption !== 'manual') {
        const found = userDecksList.find(d => String(d.id) === String(selectedDeckOption));
        if (found) {
          finalDeckId = found.id;
          snapshotDeckData = {
            id: found.id,
            name: found.name,
            leader: found.leader_name || found.commander_name || null,
            leader_image: found.leader_image || null,
            cards_count: Array.isArray(found.cards) ? found.cards.length : 0
          };
        }
      }

      const payload = {
        name: formTournamentName.trim(),
        event_category_id: Number(formEventCategoryId),
        format_id: formFormatId ? Number(formFormatId) : null,
        tournament_type_id: formTournamentTypeId ? Number(formTournamentTypeId) : null,
        tourney_set_id: formTourneySetId ? Number(formTourneySetId) : null,
        game_id: Number(formGameId),
        played_at: formPlayedAt,
        wins: Number(formWins) || 0,
        losses: Number(formLosses) || 0,
        draws: Number(formDraws) || 0,
        placement: formPlacement ? Number(formPlacement) : null,
        total_players: formTotalPlayers ? Number(formTotalPlayers) : null,
        deck_id: finalDeckId,
        deck_name: formDeckName.trim() || (finalDeckId ? 'Meu Deck' : null),
        deck_data: snapshotDeckData,
        rounds: formRounds
      };

      const saved = await saveUserTournamentReport(payload, editingReportId || undefined);

      if (saved) {
        showNotification(
          editingReportId ? 'Report de torneio atualizado com sucesso!' : 'Report de torneio salvo com sucesso!',
          'success'
        );

        // Atualizar lista em tempo real
        setReports(prev => {
          const idx = prev.findIndex(r => String(r.id) === String(saved.id));
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = saved;
            return next;
          }
          return [saved, ...prev];
        });

        setIsModalOpen(false);
      }
    } catch (err: any) {
      console.error('Error saving tournament report:', err);
      showNotification(err.message || 'Erro ao salvar report de torneio.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Excluir report (Abre modal de confirmação customizado)
  const handleRequestDeleteReport = (rep: UserTournamentReport) => {
    if (!user?.id) {
      showNotification('Você precisa estar conectado para excluir reports.', 'error');
      return;
    }
    if (!isReportOwner(rep) && user?.role !== 'admin') {
      showNotification('Você só tem permissão para excluir os seus próprios reports.', 'error');
      return;
    }
    setReportToDelete(rep);
  };

  const handleConfirmDeleteReport = async () => {
    if (!reportToDelete) return;
    setIsDeleting(true);
    try {
      await deleteUserTournamentReport(reportToDelete.id);
      setReports(prev => prev.filter(r => String(r.id) !== String(reportToDelete.id)));
      showNotification('Report excluído com sucesso!', 'success');
      setReportToDelete(null);
    } catch (err: any) {
      console.error('Error deleting report:', err);
      showNotification(err?.message || 'Erro ao excluir report de torneio.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  // Helper para obter nome da categoria
  const getCategoryName = (categoryId?: number | string | null) => {
    if (!categoryId) return null;
    const cat = eventCategories.find(c => String(c.id) === String(categoryId));
    return cat ? cat.name : null;
  };

  // Helper para obter nome do tipo de torneio
  const getTypeName = (typeId?: number | string | null) => {
    if (!typeId) return null;
    const t = tournamentTypes.find(item => String(item.id) === String(typeId));
    return t ? t.name : null;
  };

  // Helper para obter nome do jogo formatado e consistente
  const getReportGameName = (rep: { game_id?: number | string; game_name?: string; games?: any }) => {
    if (rep.game_id !== undefined && rep.game_id !== null) {
      const foundInDb = dbGames.find(g => String(g.id) === String(rep.game_id));
      if (foundInDb?.name) return foundInDb.name;
    }
    if (rep.games?.name) return rep.games.name;
    if (Array.isArray(rep.games) && rep.games[0]?.name) return rep.games[0].name;
    if (rep.game_name && rep.game_name !== 'Cardgame' && rep.game_name !== 'TCG' && isNaN(Number(rep.game_name))) {
      return rep.game_name;
    }
    if (rep.game_id !== undefined && rep.game_id !== null) {
      const fromRules = getGameNameById(rep.game_id);
      if (fromRules && isNaN(Number(fromRules))) return fromRules;
    }
    return rep.game_name || 'Cardgame';
  };

  // Alternar visualização de rodadas no card
  const toggleRoundsExpand = (reportId: string | number) => {
    setExpandedRounds(prev => ({
      ...prev,
      [String(reportId)]: !prev[String(reportId)]
    }));
  };

  // Reports filtrados
  const filteredReports = useMemo(() => {
    return reports.filter(r => {
      // Filtro de aba: "Meus Reports" filtra estritamente pelo usuário logado
      if (activeTab === 'my-reports') {
        if (!user?.id) return false;
        if (!isReportOwner(r)) return false;
      }

      // Filtro de jogo obrigatório (requer que um jogo esteja selecionado, jamais 'All')
      const gameTarget = Number(selectedGameFilter);
      if (r.game_id && Number(r.game_id) !== gameTarget) {
        return false;
      }

      // Filtro de Categoria
      if (selectedCategoryFilter !== 'All' && String(r.event_category_id) !== String(selectedCategoryFilter)) {
        return false;
      }

      // Filtro de Coleção / Set
      if (selectedSetFilter !== 'All') {
        if (!r.tourney_set_id || String(r.tourney_set_id) !== String(selectedSetFilter)) {
          return false;
        }
      }

      // Filtro de colocação / record
      if (selectedStandingFilter === 'champion') {
        const p = Number(r.placement);
        if (p !== 1) return false;
      } else if (selectedStandingFilter === 'topcut') {
        const p = Number(r.placement);
        if (!p || p > 8) return false;
      } else if (selectedStandingFilter === 'positive') {
        if (Number(r.wins) < Number(r.losses)) return false;
      }

      // Busca textual
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesName = (r.name || '').toLowerCase().includes(q);
        const matchesDeck = (r.deck_name || '').toLowerCase().includes(q);
        const matchesGame = getReportGameName(r).toLowerCase().includes(q);
        const matchesSet = (r.tourney_set_name || '').toLowerCase().includes(q);
        const catName = getCategoryName(r.event_category_id)?.toLowerCase() || '';
        const matchesCat = catName.includes(q);
        const matchesAuthor = (r.user_name || '').toLowerCase().includes(q);
        return matchesName || matchesDeck || matchesGame || matchesSet || matchesCat || matchesAuthor;
      }

      return true;
    });
  }, [reports, activeTab, selectedGameFilter, activeGame, selectedCategoryFilter, selectedSetFilter, selectedStandingFilter, searchQuery, user?.id, user?.auth_id, eventCategories, dbGames]);

  // Estatísticas calculadas contextualizadas
  const stats = useMemo(() => {
    const targetReports = activeTab === 'my-reports'
      ? (user?.id ? reports.filter(r => isReportOwner(r)) : [])
      : reports;

    const totalTournaments = targetReports.length;
    let totalWins = 0;
    let totalLosses = 0;
    let totalDraws = 0;
    let championsCount = 0;
    let topCutsCount = 0;

    targetReports.forEach(r => {
      totalWins += Number(r.wins) || 0;
      totalLosses += Number(r.losses) || 0;
      totalDraws += Number(r.draws) || 0;

      const p = Number(r.placement);
      if (p === 1) championsCount++;
      if (p >= 1 && p <= 8) topCutsCount++;
    });

    const totalMatches = totalWins + totalLosses + totalDraws;
    const winRate = totalMatches > 0 ? ((totalWins / totalMatches) * 100).toFixed(1) : '0.0';

    return {
      totalTournaments,
      totalMatches,
      totalWins,
      totalLosses,
      totalDraws,
      winRate,
      championsCount,
      topCutsCount
    };
  }, [reports, activeTab, user?.id, user?.auth_id]);

  // Renderizar badge de colocação
  const renderPlacementBadge = (placement?: number | string | null) => {
    if (!placement) return null;
    const p = Number(placement);

    if (p === 1) {
      return (
        <span className="px-2.5 py-1 rounded-xl text-xs font-black bg-amber-500/20 text-amber-300 border border-amber-500/40 shadow-sm flex items-center space-x-1.5 whitespace-nowrap">
          <i className="fas fa-crown text-amber-400"></i>
          <span>1º Lugar (Campeão)</span>
        </span>
      );
    }
    if (p === 2) {
      return (
        <span className="px-2.5 py-1 rounded-xl text-xs font-black bg-slate-300/20 text-slate-200 border border-slate-400/40 shadow-sm flex items-center space-x-1.5 whitespace-nowrap">
          <i className="fas fa-medal text-slate-300"></i>
          <span>2º Lugar (Vice)</span>
        </span>
      );
    }
    if (p <= 4) {
      return (
        <span className="px-2.5 py-1 rounded-xl text-xs font-black bg-purple-500/20 text-purple-300 border border-purple-500/40 shadow-sm flex items-center space-x-1.5 whitespace-nowrap">
          <i className="fas fa-medal text-purple-400"></i>
          <span>Top 4</span>
        </span>
      );
    }
    if (p <= 8) {
      return (
        <span className="px-2.5 py-1 rounded-xl text-xs font-black bg-blue-500/20 text-blue-300 border border-blue-500/40 shadow-sm flex items-center space-x-1.5 whitespace-nowrap">
          <i className="fas fa-medal text-blue-400"></i>
          <span>Top 8</span>
        </span>
      );
    }
    return (
      <span className="px-2.5 py-1 rounded-xl text-xs font-bold bg-slate-800 text-slate-300 border border-slate-700 whitespace-nowrap">
        {p}º Lugar
      </span>
    );
  };

  // Se nenhum jogo estiver selecionado (activeGame === 'All'), exibe o seletor padronizado
  if (activeGame === 'All') {
    return (
      <div className="min-h-[75vh] flex items-center justify-center">
        <GameSelector
          games={dbGames}
          onSelectGame={(id) => {
            if (setActiveGame) setActiveGame(id as number);
            setSelectedGameFilter(Number(id));
            setFormGameId(id as number);
          }}
          title="Escolha um Jogo"
          subtitle="Para explorar os reports de torneios e estatísticas competitivas, selecione uma franquia de TCG:"
        />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6 animate-in fade-in duration-500 px-2 sm:px-4 pb-16">
      {/* Header Principal Padronizado */}
      <div className="border-b border-slate-800/80 pb-4 sm:pb-6 space-y-3 sm:space-y-0">
        <div className="flex items-start sm:items-center justify-between gap-2.5 sm:gap-4">
          <div className="flex items-start sm:items-center space-x-2.5 sm:space-x-3 min-w-0 flex-1">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white shadow-lg shadow-orange-500/20 shrink-0">
              <i className="fas fa-trophy text-base sm:text-lg"></i>
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-black text-white tracking-tight">Diário de Torneios</h1>
              <p className="text-[11px] sm:text-xs md:text-sm text-slate-400 mt-0.5 line-clamp-2">
                Registre seus resultados, tipo de torneio, set do jogo e rodadas detalhadas.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-2.5 shrink-0 pt-0.5 sm:pt-0">
            {/* Botão Trocar Jogo no canto superior direito */}
            <button
              onClick={() => {
                if (setActiveGame) setActiveGame('All');
              }}
              className="flex items-center space-x-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-300 hover:text-white text-xs font-bold transition-all cursor-pointer shadow-sm active:scale-95 whitespace-nowrap"
              title="Voltar para a seleção de todos os jogos"
            >
              <i className="fa-solid fa-shapes text-[10px] text-purple-400"></i>
              <span>Trocar Jogo</span>
            </button>

            {/* Desktop: Novo Report ao lado */}
            <button
              onClick={() => handleOpenModal()}
              className="hidden sm:flex items-center justify-center space-x-2 px-3.5 sm:px-5 py-1.5 sm:py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-lg shadow-purple-600/20 transition-all hover:scale-[1.02] active:scale-[0.98] cursor-pointer whitespace-nowrap"
            >
              <i className="fas fa-plus"></i>
              <span>Novo Report</span>
            </button>
          </div>
        </div>

        {/* Mobile: Novo Report em linha própria destacada e acessível */}
        <div className="sm:hidden pt-1">
          <button
            onClick={() => handleOpenModal()}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 active:from-purple-500 active:to-pink-500 text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-lg shadow-purple-600/20 transition-all active:scale-[0.98] cursor-pointer"
          >
            <i className="fas fa-plus"></i>
            <span>Novo Report</span>
          </button>
        </div>
      </div>

      {/* KPI Cards de Resumo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4">
        <div className="bg-slate-900/60 border border-slate-800/80 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-center space-x-2.5 sm:space-x-4 min-w-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-purple-600/10 border border-purple-500/20 flex items-center justify-center text-purple-400 text-base sm:text-xl shrink-0">
            <i className="fas fa-flag-checkered"></i>
          </div>
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-slate-500 truncate">Torneios</p>
            <p className="text-lg sm:text-2xl font-black text-white">{stats.totalTournaments}</p>
          </div>
        </div>

        <div className="bg-slate-900/60 border border-slate-800/80 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-center space-x-2.5 sm:space-x-4 min-w-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-emerald-600/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 text-base sm:text-xl shrink-0">
            <i className="fas fa-chart-line"></i>
          </div>
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-slate-500 truncate">Win Rate</p>
            <p className="text-lg sm:text-2xl font-black text-emerald-400">{stats.winRate}%</p>
            <p className="text-[9px] sm:text-[10px] text-slate-500 font-semibold truncate">{stats.totalWins}V-{stats.totalLosses}D-{stats.totalDraws}E</p>
          </div>
        </div>

        <div className="bg-slate-900/60 border border-slate-800/80 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-center space-x-2.5 sm:space-x-4 min-w-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-amber-600/10 border border-amber-500/20 flex items-center justify-center text-amber-400 text-base sm:text-xl shrink-0">
            <i className="fas fa-crown"></i>
          </div>
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-slate-500 truncate">Títulos (1º)</p>
            <p className="text-lg sm:text-2xl font-black text-amber-400">{stats.championsCount}x</p>
          </div>
        </div>

        <div className="bg-slate-900/60 border border-slate-800/80 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-center space-x-2.5 sm:space-x-4 min-w-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center text-blue-400 text-base sm:text-xl shrink-0">
            <i className="fas fa-medal"></i>
          </div>
          <div className="min-w-0">
            <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider text-slate-500 truncate">Top Cuts (Top 8)</p>
            <p className="text-lg sm:text-2xl font-black text-blue-400">{stats.topCutsCount}x</p>
          </div>
        </div>
      </div>

      {/* Barra de Filtros e Busca */}
      <div className="bg-slate-900/40 p-3 sm:p-4 rounded-2xl border border-slate-800/80 space-y-3">
        {/* Campo de Busca textual */}
        <div className="relative w-full">
          <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs pointer-events-none"></i>
          <input
            type="text"
            placeholder="Buscar torneio, deck, set..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-9 pr-8 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 transition-colors"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white text-xs p-1.5 cursor-pointer"
              title="Limpar busca"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>

        {/* Grade de 4 Seletores */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
          {/* 1. Alternador de Abas (Todos e Meus Reports) */}
          <div className="flex items-center space-x-1.5 bg-slate-950 p-1 rounded-xl border border-slate-800 h-[42px]">
            <button
              type="button"
              onClick={() => setActiveTab('all')}
              className={`flex-1 h-full text-center px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center justify-center truncate ${
                activeTab === 'all'
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                  : 'text-slate-400 hover:text-white hover:bg-slate-900'
              }`}
            >
              Todos
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('my-reports')}
              className={`flex-1 h-full text-center px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center justify-center truncate ${
                activeTab === 'my-reports'
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                  : 'text-slate-400 hover:text-white hover:bg-slate-900'
              }`}
            >
              Meus Reports
            </button>
          </div>

          {/* 2. Filtro: Coleção / Set */}
          <div className="relative group">
            <select
              value={selectedSetFilter}
              onChange={e => setSelectedSetFilter(e.target.value)}
              className="w-full h-[42px] bg-slate-950/80 border border-slate-800 hover:border-slate-700 text-white text-xs rounded-xl px-3.5 py-2.5 cursor-pointer appearance-none focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/50 transition-all font-bold uppercase tracking-wider pr-8 truncate"
              title="Filtrar por Coleção"
            >
              <option value="All">
                {filterGameSets.length === 0 ? 'NENHUMA COLEÇÃO' : 'TODAS AS COLEÇÕES'}
              </option>
              {filterGameSets.map(s => (
                <option key={`filter-set-${s.id}`} value={String(s.id)}>
                  {s.name.toUpperCase()} {s.code ? `(${s.code})` : ''}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500 group-focus-within:text-purple-400 transition-colors">
              <i className="fas fa-chevron-down text-[10px]"></i>
            </div>
          </div>

          {/* 3. Filtro: Categoria do Evento */}
          <div className="relative group">
            <select
              value={selectedCategoryFilter}
              onChange={e => setSelectedCategoryFilter(e.target.value)}
              className="w-full h-[42px] bg-slate-950/80 border border-slate-800 hover:border-slate-700 text-white text-xs rounded-xl px-3.5 py-2.5 cursor-pointer appearance-none focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/50 transition-all font-bold uppercase tracking-wider pr-8 truncate"
            >
              <option value="All">TODAS AS CATEGORIAS</option>
              {eventCategories.map(c => (
                <option key={`filter-cat-${c.id}`} value={c.id}>
                  {c.name.toUpperCase()}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500 group-focus-within:text-purple-400 transition-colors">
              <i className="fas fa-chevron-down text-[10px]"></i>
            </div>
          </div>

          {/* 4. Filtro: Colocação */}
          <div className="relative group">
            <select
              value={selectedStandingFilter}
              onChange={e => setSelectedStandingFilter(e.target.value as any)}
              className="w-full h-[42px] bg-slate-950/80 border border-slate-800 hover:border-slate-700 text-white text-xs rounded-xl px-3.5 py-2.5 cursor-pointer appearance-none focus:outline-none focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/50 transition-all font-bold uppercase tracking-wider pr-8 truncate"
            >
              <option value="all">TODAS AS COLOCAÇÕES</option>
              <option value="champion">🏆 CAMPEÃO (1º LUGAR)</option>
              <option value="topcut">🥇 TOP CUT (TOP 8)</option>
              <option value="positive">📈 RECORD POSITIVO (W &gt; L)</option>
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500 group-focus-within:text-purple-400 transition-colors">
              <i className="fas fa-chevron-down text-[10px]"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Reports */}
      {isLoadingReports ? (
        <div className="py-16 text-center space-y-3">
          <div className="w-10 h-10 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin mx-auto"></div>
          <p className="text-xs text-slate-400 font-medium">Carregando reports de torneios...</p>
        </div>
      ) : filteredReports.length === 0 ? (
        <div className="bg-slate-900/30 border border-dashed border-slate-800 rounded-3xl p-12 text-center space-y-4">
          <div className="w-16 h-16 rounded-2xl bg-slate-800/50 flex items-center justify-center text-slate-500 text-2xl mx-auto">
            <i className="fas fa-clipboard-list"></i>
          </div>
          <div className="max-w-md mx-auto">
            <h3 className="text-lg font-bold text-white mb-1">
              {activeTab === 'my-reports' && !user?.id
                ? 'Conecte-se para ver seus reports'
                : 'Nenhum report encontrado'}
            </h3>
            <p className="text-xs text-slate-400 mb-6">
              {activeTab === 'my-reports' && !user?.id
                ? 'Faça login na sua conta Cardumy para registrar e acompanhar o histórico dos seus torneios.'
                : 'Registre agora o seu resultado de torneio informando o tipo, set do jogo, placar e rodadas!'}
            </p>
            {user?.id ? (
              <button
                onClick={() => handleOpenModal()}
                className="px-6 py-2.5 bg-purple-600 hover:bg-purple-500 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-lg shadow-purple-600/20"
              >
                Criar Primeiro Report
              </button>
            ) : (
              <button
                onClick={() => handleOpenModal()}
                className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-lg shadow-purple-600/20"
              >
                Fazer Login / Registrar
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredReports.map((rep, rIdx) => {
            const isOwner = isReportOwner(rep);
            const canManage = isOwner || user?.role === 'admin';
            const winCount = Number(rep.wins) || 0;
            const lossCount = Number(rep.losses) || 0;
            const drawCount = Number(rep.draws) || 0;
            const totalM = winCount + lossCount + drawCount;
            const wr = totalM > 0 ? ((winCount / totalM) * 100).toFixed(0) : '0';
            const categoryName = rep.event_category_name || getCategoryName(rep.event_category_id);
            const gameDisplayName = getReportGameName(rep);
            const formatName = rep.format_name || tournamentFormats.find(f => String(f.id) === String(rep.format_id))?.name || null;
            const typeName = rep.tournament_type_name || getTypeName(rep.tournament_type_id);
            const hasRounds = Array.isArray(rep.rounds) && rep.rounds.length > 0;
            const isExpanded = !!expandedRounds[String(rep.id)];

            return (
              <div
                key={`report-item-${rep.id ?? rIdx}-${rIdx}`}
                className="bg-slate-900/70 border border-slate-800/80 hover:border-slate-700/80 rounded-2xl overflow-hidden transition-all shadow-lg"
              >
                <div className="p-3.5 sm:p-5">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
                    {/* Informações Principais */}
                    <div className="space-y-1.5 sm:space-y-2 flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                        {/* Game Badge */}
                        <span className="px-2 sm:px-2.5 py-0.5 rounded-md bg-purple-600/10 border border-purple-500/20 text-purple-400 text-[9px] sm:text-[10px] font-black uppercase tracking-wider flex items-center space-x-1.5">
                          <i className={`fas ${getGameIcon(gameDisplayName || rep.game_id)}`}></i>
                          <span>{gameDisplayName}</span>
                        </span>

                        {/* Event Category Badge */}
                        {categoryName && (
                          <span className="px-2 sm:px-2.5 py-0.5 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[9px] sm:text-[10px] font-black uppercase tracking-wider flex items-center space-x-1">
                            <i className="fas fa-tag text-[8px] sm:text-[9px] text-amber-400"></i>
                            <span>{categoryName}</span>
                          </span>
                        )}

                        {/* Tournament Format Badge */}
                        {formatName && (
                          <span className="px-2 sm:px-2.5 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[9px] sm:text-[10px] font-black uppercase tracking-wider flex items-center space-x-1">
                            <i className="fas fa-gamepad text-[8px] sm:text-[9px] text-emerald-400"></i>
                            <span>{formatName}</span>
                          </span>
                        )}

                        {/* Tournament Type Badge */}
                        {typeName && (
                          <span className="px-2 sm:px-2.5 py-0.5 rounded-md bg-blue-500/15 border border-blue-500/30 text-blue-300 text-[9px] sm:text-[10px] font-black uppercase tracking-wider flex items-center space-x-1">
                            <i className="fas fa-sitemap text-[8px] sm:text-[9px] text-blue-400"></i>
                            <span>{typeName}</span>
                          </span>
                        )}

                        {/* Game Set Badge */}
                        {rep.tourney_set_name && (
                          <span className="px-2 sm:px-2.5 py-0.5 rounded-md bg-indigo-500/15 border border-indigo-500/30 text-indigo-300 text-[9px] sm:text-[10px] font-black uppercase tracking-wider flex items-center space-x-1">
                            <i className="fas fa-box-open text-[8px] sm:text-[9px] text-indigo-400"></i>
                            <span>{rep.tourney_set_name}</span>
                          </span>
                        )}

                        {/* Total de Jogadores */}
                        {rep.total_players && (
                          <span className="text-[9px] sm:text-[10px] font-semibold text-slate-400 bg-slate-800/50 px-2 py-0.5 rounded-md">
                            {rep.total_players} jogadores
                          </span>
                        )}

                        {/* Author / Owner Badge */}
                        {isOwner ? (
                          <span className="px-2 sm:px-2.5 py-0.5 rounded-md bg-purple-500/20 border border-purple-500/40 text-purple-300 text-[9px] sm:text-[10px] font-black uppercase tracking-wider flex items-center space-x-1">
                            <i className="fas fa-user-check text-[8px] sm:text-[9px]"></i>
                            <span>Seu Report</span>
                          </span>
                        ) : (
                          <span className="px-2 sm:px-2.5 py-0.5 rounded-md bg-slate-800/80 border border-slate-700/80 text-slate-300 text-[9px] sm:text-[10px] font-semibold flex items-center space-x-1.5">
                            {rep.user_avatar ? (
                              <img src={rep.user_avatar} alt="" className="w-3.5 h-3.5 rounded-full object-cover" />
                            ) : (
                              <i className="fas fa-user text-[8px] sm:text-[9px] text-slate-400"></i>
                            )}
                            <span className="truncate max-w-[130px]">{rep.user_name || 'Jogador'}</span>
                          </span>
                        )}
                      </div>

                      <div className="flex items-center space-x-2 sm:space-x-3">
                        <h2 className="text-base sm:text-lg font-black text-white tracking-tight break-words">{rep.name}</h2>
                      </div>

                      {/* Metadados: Data e Deck */}
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-[11px] sm:text-xs text-slate-400">
                        <span className="flex items-center space-x-1">
                          <i className="far fa-calendar text-slate-500"></i>
                          <span>{new Date(rep.played_at).toLocaleDateString('pt-BR')}</span>
                        </span>

                        {rep.deck_name && (
                          <span className="flex items-center space-x-1.5 bg-purple-950/40 border border-purple-800/40 px-2 py-0.5 rounded-md text-purple-300 font-semibold text-[10px] sm:text-[11px] max-w-full truncate">
                            <i className="fas fa-layer-group text-purple-400 text-[9px] sm:text-[10px]"></i>
                            <span className="truncate">Deck: {rep.deck_name}</span>
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Placar, Colocação e Ações */}
                    <div className="flex flex-wrap sm:flex-nowrap items-center justify-between lg:justify-end gap-2.5 sm:gap-4 pt-2.5 lg:pt-0 border-t lg:border-t-0 border-slate-800/60">
                      {/* Colocação */}
                      {renderPlacementBadge(rep.placement)}

                      {/* Placar Record */}
                      <div className="text-right">
                        <p className="text-xs sm:text-sm font-black text-white">
                          <span className="text-emerald-400">{winCount}V</span>
                          <span className="text-slate-500 mx-0.5 sm:mx-1">-</span>
                          <span className="text-rose-400">{lossCount}D</span>
                          {drawCount > 0 && (
                            <>
                              <span className="text-slate-500 mx-0.5 sm:mx-1">-</span>
                              <span className="text-amber-400">{drawCount}E</span>
                            </>
                          )}
                        </p>
                        <p className="text-[9px] sm:text-[10px] text-slate-500 font-bold">{wr}% Winrate</p>
                      </div>

                      {/* Botões de Ação */}
                      <div className="flex items-center space-x-1 sm:space-x-1.5 sm:pl-2">
                        {hasRounds && (
                          <button
                            onClick={() => toggleRoundsExpand(rep.id)}
                            className={`px-2 sm:px-2.5 py-1.5 rounded-xl text-[11px] sm:text-xs font-bold transition-all flex items-center space-x-1 sm:space-x-1.5 ${
                              isExpanded
                                ? 'bg-purple-600 text-white'
                                : 'bg-slate-800/80 hover:bg-slate-800 text-slate-300 hover:text-white'
                            }`}
                            title="Ver rodadas detalhadas"
                          >
                            <i className="fas fa-list-ol text-[10px] sm:text-[11px]"></i>
                            <span>{rep.rounds?.length} {rep.rounds?.length === 1 ? 'Rodada' : 'Rodadas'}</span>
                            <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-[8px] sm:text-[9px] ml-0.5`}></i>
                          </button>
                        )}

                        {canManage && (
                          <>
                            <button
                              onClick={() => handleOpenModal(rep)}
                              className="p-1.5 sm:p-2 hover:bg-slate-800 text-slate-400 hover:text-white rounded-xl text-xs transition-colors"
                              title="Editar report"
                            >
                              <i className="fas fa-pen-to-square"></i>
                            </button>

                            <button
                              onClick={() => handleRequestDeleteReport(rep)}
                              className="p-1.5 sm:p-2 hover:bg-rose-950/40 text-slate-400 hover:text-rose-400 rounded-xl text-xs transition-colors"
                              title="Excluir report"
                            >
                              <i className="fas fa-trash-can"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Seção Expansível de Rodadas Detalhadas */}
                  {hasRounds && isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-slate-800/80 space-y-2.5"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] sm:text-xs font-black uppercase tracking-wider text-slate-400 flex items-center space-x-1.5">
                          <i className="fas fa-list-check text-purple-400"></i>
                          <span>Histórico de Partidas por Rodada</span>
                        </span>
                        <span className="text-[10px] sm:text-[11px] text-slate-500 font-semibold">
                          {rep.rounds?.length} rodadas
                        </span>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-2.5">
                        {rep.rounds?.map((round, rIdx) => {
                          const isWin = round.result === 'win';
                          const isLoss = round.result === 'loss';
                          const isDraw = round.result === 'draw';

                          return (
                            <div
                              key={`rep-${rep.id}-round-${round.id || rIdx}-${round.round_number || rIdx + 1}-${rIdx}`}
                              className={`p-3 rounded-xl border flex flex-col justify-between space-y-2 transition-all ${
                                isWin
                                  ? 'bg-emerald-950/20 border-emerald-500/30'
                                  : isLoss
                                  ? 'bg-rose-950/20 border-rose-500/30'
                                  : 'bg-amber-950/20 border-amber-500/30'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-black text-white flex items-center space-x-1.5">
                                  <span className="w-5 h-5 rounded-full bg-slate-800 flex items-center justify-center text-[10px] text-slate-300 font-black">
                                    {round.round_number || rIdx + 1}
                                  </span>
                                  <span>Rodada {round.round_number || rIdx + 1}</span>
                                </span>

                                <span
                                  className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider ${
                                    isWin
                                      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                                      : isLoss
                                      ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40'
                                      : 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                                  }`}
                                >
                                  {isWin ? 'Vitória (W)' : isLoss ? 'Derrota (L)' : 'Empate (D)'}
                                </span>
                              </div>

                              <div className="space-y-1 text-xs">
                                <p className="text-slate-300 font-semibold truncate flex items-center space-x-1.5">
                                  <i className="fas fa-user-ninja text-slate-500 text-[10px]"></i>
                                  <span>{round.opponent_name || 'Oponente'}</span>
                                </p>

                                {round.opponent_deck && (
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    <div className="flex items-center space-x-2 min-w-0">
                                      {(() => {
                                        const deckThumb = getRoundDeckImage(round);
                                        return deckThumb ? (
                                          <img
                                            src={deckThumb}
                                            alt={round.opponent_deck || 'Deck'}
                                            className="w-5 h-5 rounded-md object-cover shrink-0 border border-purple-500/40 bg-slate-900 shadow-sm ring-1 ring-purple-500/20"
                                            onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
                                          />
                                        ) : (
                                          <span className="w-5 h-5 rounded-md bg-purple-950/60 border border-purple-800/40 flex items-center justify-center shrink-0 text-[10px] text-purple-400 shadow-sm">
                                            <i className="fas fa-layer-group text-[9px]"></i>
                                          </span>
                                        );
                                      })()}
                                      <p className="text-purple-300 text-[11px] font-bold truncate flex items-center space-x-1">
                                        <span>{round.opponent_deck}</span>
                                      </p>
                                    </div>

                                    {round.opponent_meta_deck?.colors && round.opponent_meta_deck.colors.length > 0 && (
                                      <div className="flex items-center gap-1">
                                        {round.opponent_meta_deck.colors.map((col, cIdx) => (
                                          <span
                                            key={`round-${round.id || rIdx}-col-${col}-${cIdx}`}
                                            className={`px-1 py-0.2 rounded text-[8px] font-black border ${getColorBadgeStyle(col)}`}
                                          >
                                            {col}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                              <div className="flex items-center justify-between text-[10px] text-slate-400 pt-1 border-t border-slate-800/40">
                                <span className="flex items-center space-x-1">
                                  {round.play_order === 1 ? (
                                    <span className="text-blue-400 font-bold">1º a Jogar (Play)</span>
                                  ) : round.play_order === 2 ? (
                                    <span className="text-indigo-400 font-bold">2º a Jogar (Draw)</span>
                                  ) : (
                                    <span className="text-slate-500">-</span>
                                  )}
                                </span>

                                {round.won_dice !== null && round.won_dice !== undefined && (
                                  <span className="flex items-center space-x-1 text-slate-400">
                                    <i className="fas fa-dice text-purple-400"></i>
                                    <span>{round.won_dice ? 'Dado Ganho' : 'Dado Perdido'}</span>
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL: SALVAR REPORT COM TIPO, SET DO JOGO E RODADAS NO SUPABASE */}
      {/* ========================================================================= */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 md:p-6 bg-black/85 backdrop-blur-sm overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="bg-slate-900 border border-slate-800 rounded-2xl sm:rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl flex flex-col my-auto max-h-[95vh]"
            >
              {/* Header do Modal */}
              <div className="p-3.5 sm:p-5 border-b border-slate-800 bg-slate-950 flex items-center justify-between">
                <div className="flex items-center space-x-2.5 sm:space-x-3 min-w-0">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white shadow-lg shadow-purple-600/20 shrink-0">
                    <i className="fas fa-trophy text-xs sm:text-sm"></i>
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-black text-white text-sm sm:text-base md:text-lg tracking-tight truncate">
                      {editingReportId ? 'Editar Report de Torneio' : 'Novo Report de Torneio'}
                    </h3>
                    <p className="text-[11px] sm:text-xs text-slate-400 truncate">
                      Preencha os dados do evento, tipo, set e rodadas disputadas.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-slate-400 hover:text-white p-1.5 sm:p-2 rounded-xl hover:bg-slate-800 transition-colors shrink-0 ml-2"
                >
                  <i className="fas fa-xmark text-base sm:text-lg"></i>
                </button>
              </div>

              {/* Corpo do Formulário */}
              <form onSubmit={handleSaveReport} className="p-3.5 sm:p-5 md:p-6 space-y-4 sm:space-y-5 overflow-y-auto max-h-[calc(95vh-130px)]">
                {/* 1. Informações Básicas do Evento */}
                <div className="space-y-3 sm:space-y-4">
                  {/* Nome do Torneio (NOT NULL) */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-300 flex items-center space-x-1">
                      <span>Nome do Torneio / Evento</span>
                      <span className="text-rose-400">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="Ex: Store Showdown, Regional SP, Semanal..."
                      value={formTournamentName}
                      onChange={e => setFormTournamentName(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 sm:px-3.5 py-2 sm:py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 transition-colors"
                    />
                  </div>

                  {/* Grid: Cardgame (OBRIGATÓRIO) e Categoria do Evento (OBRIGATÓRIA) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-300 flex items-center space-x-1">
                        <span>Cardgame</span>
                        <span className="text-rose-400">*</span>
                      </label>
                      <select
                        required
                        value={formGameId}
                        onChange={e => setFormGameId(Number(e.target.value))}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 sm:px-3.5 py-2 sm:py-2.5 text-xs text-white focus:outline-none focus:border-purple-500 transition-colors"
                      >
                        {dbGames.map(g => (
                          <option key={`form-game-${g.id}`} value={g.id}>
                            {g.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-300 flex items-center space-x-1">
                        <span>Categoria do Evento</span>
                        <span className="text-rose-400">*</span>
                      </label>
                      <select
                        required
                        value={formEventCategoryId}
                        onChange={e => setFormEventCategoryId(e.target.value)}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 sm:px-3.5 py-2 sm:py-2.5 text-xs text-white focus:outline-none focus:border-purple-500 transition-colors"
                      >
                        {eventCategories.map(c => (
                          <option key={`form-cat-${c.id}`} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Grid: Formato, Tipo de Torneio e Set do Jogo */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-300 flex items-center space-x-1.5">
                        <i className="fas fa-gamepad text-emerald-400"></i>
                        <span>Formato</span>
                      </label>
                      <select
                        value={formFormatId}
                        onChange={e => setFormFormatId(e.target.value)}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 sm:px-3.5 py-2 sm:py-2.5 text-xs text-white focus:outline-none focus:border-purple-500 transition-colors"
                      >
                        <option value="">Nenhum / Não especificado</option>
                        {tournamentFormats.map(f => (
                          <option key={`form-format-${f.id}`} value={f.id}>
                            {f.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-300 flex items-center space-x-1.5">
                        <i className="fas fa-sitemap text-purple-400"></i>
                        <span>Tipo de Torneio</span>
                      </label>
                      <select
                        value={formTournamentTypeId}
                        onChange={e => setFormTournamentTypeId(e.target.value)}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 sm:px-3.5 py-2 sm:py-2.5 text-xs text-white focus:outline-none focus:border-purple-500 transition-colors"
                      >
                        {tournamentTypes.map(t => (
                          <option key={`form-type-${t.id}`} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-300 flex items-center space-x-1.5">
                        <i className="fas fa-box-open text-indigo-400"></i>
                        <span>Set / Coleção</span>
                      </label>
                      <select
                        value={formTourneySetId}
                        onChange={e => setFormTourneySetId(e.target.value)}
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 sm:px-3.5 py-2 sm:py-2.5 text-xs text-white focus:outline-none focus:border-purple-500 transition-colors"
                      >
                        <option value="">
                          {gameSets.length === 0 ? 'Nenhum set disponível' : 'Nenhum / Não especificado'}
                        </option>
                        {gameSets.map(s => (
                          <option key={`form-set-${s.id}`} value={s.id}>
                            {s.name} {s.code ? `(${s.code})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Data do Torneio (OBRIGATÓRIA) */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-300 flex items-center space-x-1">
                      <span>Data do Torneio</span>
                      <span className="text-rose-400">*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={formPlayedAt}
                      onChange={e => setFormPlayedAt(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 sm:px-3.5 py-2 sm:py-2.5 text-xs text-white focus:outline-none focus:border-purple-500 transition-colors"
                    />
                  </div>
                </div>

                {/* 2. Rodadas do Torneio (Interactive Match Builder) */}
                <div className="bg-slate-950/80 border border-slate-800 rounded-2xl p-3 sm:p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-black uppercase tracking-wider text-slate-300 flex items-center space-x-1.5">
                      <i className="fas fa-list-ol text-purple-400"></i>
                      <span>Rodadas ({formRounds.length})</span>
                    </label>
                    <button
                      type="button"
                      onClick={handleAddRound}
                      className="px-2.5 sm:px-3 py-1 sm:py-1.5 bg-purple-600/30 hover:bg-purple-600/50 border border-purple-500/40 text-purple-300 rounded-xl text-[11px] sm:text-xs font-black flex items-center space-x-1.5 transition-all"
                    >
                      <i className="fas fa-plus text-[10px]"></i>
                      <span>Adicionar Rodada</span>
                    </button>
                  </div>

                  {formRounds.length === 0 ? (
                    <div className="bg-slate-900/50 border border-dashed border-slate-800/80 rounded-xl p-3.5 sm:p-4 text-center space-y-2">
                      <p className="text-xs text-slate-400">
                        Nenhuma rodada adicionada ainda. Clique em <strong>Adicionar Rodada</strong> para detalhar suas partidas ou use o placar abaixo.
                      </p>
                      <button
                        type="button"
                        onClick={handleAddRound}
                        className="text-xs text-purple-400 hover:text-purple-300 font-bold underline"
                      >
                        + Adicionar Rodada 1
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2.5 max-h-[300px] overflow-y-auto pr-0.5 sm:pr-1">
                      {formRounds.map((round, idx) => (
                        <div
                          key={`form-round-item-${round.id || round.round_number || idx + 1}-${idx}`}
                          className="bg-slate-900 border border-slate-800/90 rounded-xl p-2.5 sm:p-3 space-y-2.5 transition-all"
                        >
                          <div className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-2">
                            <span className="text-xs font-black text-white flex items-center space-x-2">
                              <span className="w-5 h-5 rounded-md bg-purple-600/20 text-purple-300 border border-purple-500/30 flex items-center justify-center text-[10px]">
                                {round.round_number || idx + 1}
                              </span>
                              <span>Rodada {round.round_number || idx + 1}</span>
                            </span>

                            {/* Resultado da Rodada (Pills Rápidos) */}
                            <div className="flex items-center space-x-1 shrink-0">
                              <button
                                type="button"
                                onClick={() => handleUpdateRound(idx, { result: 'win' })}
                                className={`px-2 sm:px-2.5 py-1 rounded-lg text-[10px] font-black uppercase transition-all ${
                                  round.result === 'win'
                                    ? 'bg-emerald-500 text-slate-950 font-black shadow-md shadow-emerald-500/20'
                                    : 'bg-slate-800 text-slate-400 hover:text-emerald-400'
                                }`}
                              >
                                Vitória
                              </button>
                              <button
                                type="button"
                                onClick={() => handleUpdateRound(idx, { result: 'loss' })}
                                className={`px-2 sm:px-2.5 py-1 rounded-lg text-[10px] font-black uppercase transition-all ${
                                  round.result === 'loss'
                                    ? 'bg-rose-500 text-white font-black shadow-md shadow-rose-500/20'
                                    : 'bg-slate-800 text-slate-400 hover:text-rose-400'
                                }`}
                              >
                                Derrota
                              </button>
                              <button
                                type="button"
                                onClick={() => handleUpdateRound(idx, { result: 'draw' })}
                                className={`px-2 sm:px-2.5 py-1 rounded-lg text-[10px] font-black uppercase transition-all ${
                                  round.result === 'draw'
                                    ? 'bg-amber-500 text-slate-950 font-black shadow-md shadow-amber-500/20'
                                    : 'bg-slate-800 text-slate-400 hover:text-amber-400'
                                }`}
                              >
                                Empate
                              </button>
                              <button
                                type="button"
                                onClick={() => handleRemoveRound(idx)}
                                className="p-1 text-slate-500 hover:text-rose-400 rounded-md transition-colors ml-0.5"
                                title="Remover rodada"
                              >
                                <i className="fas fa-trash-can text-xs"></i>
                              </button>
                            </div>
                          </div>

                          {/* Inputs de Oponente e Deck do Oponente com Meta Decks Sugeridos */}
                          <div className="space-y-2">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                              {/* Nome do oponente */}
                              <div className="relative">
                                <label className="block text-[10px] text-slate-400 font-semibold mb-1">
                                  Oponente
                                </label>
                                <input
                                  type="text"
                                  placeholder="Nome (opcional)"
                                  value={round.opponent_name || ''}
                                  onChange={e => handleUpdateRound(idx, { opponent_name: e.target.value })}
                                  className="w-full bg-slate-950 border border-slate-800 rounded-lg px-2.5 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                                />
                              </div>

                              {/* Elemento select com dados da view public.v_tournament_meta_decks_stats */}
                              <div className="relative">
                                <label className="block text-[10px] text-slate-400 font-semibold mb-1 flex items-center justify-between">
                                  <span>Meta Deck</span>
                                  {round.opponent_meta_deck_id && (
                                    <span className="text-[9px] text-purple-400 font-bold">✓ Selecionado</span>
                                  )}
                                </label>
                                <select
                                  value={round.opponent_meta_deck_id ? String(round.opponent_meta_deck_id) : ''}
                                  onChange={e => {
                                    const val = e.target.value;
                                    if (!val) {
                                      handleSelectMetaDeck(idx, null);
                                    } else {
                                      const selected = tournamentMetaDecks.find(m => String(m.id) === val);
                                      if (selected) handleSelectMetaDeck(idx, selected);
                                    }
                                  }}
                                  className="w-full bg-slate-950 border border-slate-800 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-purple-500 font-medium"
                                >
                                  <option value="">-- Selecionar Meta Deck --</option>
                                  {tournamentMetaDecks.map((meta, mIdx) => {
                                    const countPartidas = meta.rounds_count ? ` (${meta.rounds_count} rounds)` : '';
                                    const codePart = meta.code ? ` [${meta.code}]` : '';
                                    return (
                                      <option key={`round-${idx}-meta-select-${meta.id ?? mIdx}-${mIdx}`} value={String(meta.id)}>
                                        {(meta.display_name || meta.name) + codePart + countPartidas}
                                      </option>
                                    );
                                  })}
                                </select>
                              </div>

                              {/* Deck rival (preenchido pelo select ou livre) */}
                              <div className="relative">
                                <label className="block text-[10px] text-slate-400 font-semibold mb-1">
                                  Nome do Deck
                                </label>
                                <div className="relative flex items-center">
                                  <input
                                    type="text"
                                    list={`meta-decks-datalist-${idx}`}
                                    placeholder="Deck rival (ex: Enel, Sabine...)"
                                    value={round.opponent_deck || ''}
                                    onChange={e => handleOpponentDeckInputChange(idx, e.target.value)}
                                    className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-2.5 pr-8 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                                  />
                                  {round.opponent_deck && (
                                    <button
                                      type="button"
                                      onClick={() => handleSelectMetaDeck(idx, null)}
                                      className="absolute right-2 text-slate-500 hover:text-rose-400 p-0.5 text-xs transition-colors"
                                      title="Limpar deck rival"
                                    >
                                      <i className="fas fa-times"></i>
                                    </button>
                                  )}
                                </div>

                                <datalist id={`meta-decks-datalist-${idx}`}>
                                  {tournamentMetaDecks.map((meta, mIdx) => (
                                    <option
                                      key={`round-${idx}-meta-datalist-${meta.id ?? mIdx}-${mIdx}`}
                                      value={meta.display_name || meta.name}
                                    >
                                      {meta.colors && meta.colors.length > 0 ? `[${meta.colors.join('/')}]` : ''}
                                    </option>
                                  ))}
                                </datalist>
                              </div>
                            </div>

                            {/* Sugestões de Meta Decks Rápidos para o jogo atual (view public.v_tournament_meta_decks_stats) */}
                            {tournamentMetaDecks.length > 0 && (() => {
                              const isExpanded = !!expandedMetaRounds[idx];
                              return (
                                <div className="space-y-1 pt-0.5">
                                  <div className="flex items-center justify-between text-[10px] text-slate-400 px-0.5">
                                    <button
                                      type="button"
                                      onClick={() => toggleMetaRounds(idx)}
                                      className="flex items-center space-x-1.5 font-semibold text-slate-400 hover:text-purple-300 transition-colors group cursor-pointer focus:outline-none"
                                    >
                                      <i className="fas fa-fire-flame-curved text-purple-400 text-[10px]"></i>
                                      <span>Meta Decks Rápidos</span>
                                      <span className="text-[9px] text-slate-500 font-normal">({tournamentMetaDecks.length})</span>
                                      <i
                                        className={`fas fa-chevron-down text-[9px] text-slate-500 group-hover:text-purple-400 transition-transform duration-200 ${
                                          isExpanded ? 'rotate-180 text-purple-400' : ''
                                        }`}
                                      ></i>
                                    </button>

                                    {round.opponent_meta_deck_id && (
                                      <span className="text-[10px] text-purple-400 font-bold flex items-center space-x-1">
                                        <i className="fas fa-check-circle"></i>
                                        <span>Meta selecionado</span>
                                      </span>
                                    )}
                                  </div>

                                  {isExpanded && (
                                    <div className="flex flex-wrap items-center gap-1.5 pt-1 max-h-48 overflow-y-auto pr-1">
                                      {tournamentMetaDecks.map((meta, mIdx) => {
                                        const isSelected = String(round.opponent_meta_deck_id) === String(meta.id) ||
                                          round.opponent_deck === (meta.display_name || meta.name) ||
                                          round.opponent_deck === meta.name;

                                        return (
                                          <button
                                            key={`round-${idx}-meta-quickbtn-${meta.id ?? mIdx}-${mIdx}`}
                                            type="button"
                                            onClick={() => handleSelectMetaDeck(idx, isSelected ? null : meta)}
                                            className={`px-2 py-1 rounded-md text-[10px] font-bold shrink-0 flex items-center space-x-1.5 transition-all border ${
                                              isSelected
                                                ? 'bg-purple-600/30 text-purple-300 border-purple-500 shadow-sm shadow-purple-500/20'
                                                : 'bg-slate-900/80 text-slate-300 border-slate-800 hover:border-purple-500/50 hover:text-white'
                                            }`}
                                            title={meta.rounds_count ? `${meta.rounds_count} rodadas registradas` : undefined}
                                          >
                                            {meta.image_url ? (
                                              <img
                                                src={meta.image_url}
                                                alt={meta.name}
                                                className="w-3.5 h-3.5 rounded-sm object-cover"
                                                onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
                                              />
                                            ) : (
                                              <i className="fas fa-layer-group text-[9px] text-purple-400"></i>
                                            )}
                                            <span>{meta.display_name || meta.name}</span>
                                            {meta.rounds_count !== undefined && Number(meta.rounds_count) > 0 && (
                                              <span className="px-1 py-0.2 rounded bg-purple-950 text-purple-300 text-[8px] font-semibold border border-purple-800/40">
                                                {meta.rounds_count}
                                              </span>
                                            )}
                                            {meta.colors && meta.colors.length > 0 && (
                                              <span className="text-[8px] opacity-75">
                                                ({meta.colors[0]})
                                              </span>
                                            )}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>

                          {/* Play/Draw e Ganhou no Dado */}
                          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 pt-1 border-t border-slate-800/40 text-xs">
                            <div className="flex items-center space-x-1.5">
                              <button
                                type="button"
                                onClick={() => handleUpdateRound(idx, { play_order: round.play_order === 1 ? null : 1 })}
                                className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                  round.play_order === 1
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-slate-800 text-slate-400 hover:text-white'
                                }`}
                              >
                                1º a Jogar (Play)
                              </button>
                              <button
                                type="button"
                                onClick={() => handleUpdateRound(idx, { play_order: round.play_order === 2 ? null : 2 })}
                                className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                  round.play_order === 2
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-slate-800 text-slate-400 hover:text-white'
                                }`}
                              >
                                2º a Jogar (Draw)
                              </button>
                            </div>

                            <label className="flex items-center space-x-1.5 cursor-pointer text-[11px] text-slate-300 select-none">
                              <input
                                type="checkbox"
                                checked={!!round.won_dice}
                                onChange={e => handleUpdateRound(idx, { won_dice: e.target.checked })}
                                className="rounded bg-slate-950 border-slate-700 text-purple-600 focus:ring-0 w-3.5 h-3.5"
                              />
                              <span>Ganhou no Dado / Par ou Ímpar</span>
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* 3. Placar Total / Record (Vitórias, Derrotas, Empates) */}
                <div className="bg-slate-950/80 border border-slate-800 rounded-2xl p-3 sm:p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-black uppercase tracking-wider text-slate-300 flex items-center space-x-1.5">
                      <i className="fas fa-trophy text-purple-400"></i>
                      <span>Record Final</span>
                    </label>
                    <span className="text-xs font-bold text-purple-300">
                      {formWins}V - {formLosses}D - {formDraws}E
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-1.5 sm:gap-3">
                    {/* Vitórias */}
                    <div className="bg-slate-900 border border-emerald-500/30 rounded-xl p-1.5 sm:p-2.5 text-center space-y-1 sm:space-y-1.5">
                      <span className="text-[10px] sm:text-[11px] font-bold text-emerald-400 uppercase">Vitórias</span>
                      <div className="flex items-center justify-center space-x-1 sm:space-x-2">
                        <button
                          type="button"
                          onClick={() => setFormWins(prev => Math.max(0, prev - 1))}
                          className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold flex items-center justify-center text-xs"
                        >
                          -
                        </button>
                        <input
                          type="number"
                          min="0"
                          value={formWins}
                          onChange={e => setFormWins(Math.max(0, parseInt(e.target.value, 10) || 0))}
                          className="w-8 sm:w-10 bg-slate-950 border border-slate-800 rounded-md py-0.5 sm:py-1 text-center text-xs font-black text-emerald-400 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => setFormWins(prev => prev + 1)}
                          className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-emerald-600/30 hover:bg-emerald-600/50 text-emerald-300 font-bold flex items-center justify-center text-xs"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* Derrotas */}
                    <div className="bg-slate-900 border border-rose-500/30 rounded-xl p-1.5 sm:p-2.5 text-center space-y-1 sm:space-y-1.5">
                      <span className="text-[10px] sm:text-[11px] font-bold text-rose-400 uppercase">Derrotas</span>
                      <div className="flex items-center justify-center space-x-1 sm:space-x-2">
                        <button
                          type="button"
                          onClick={() => setFormLosses(prev => Math.max(0, prev - 1))}
                          className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold flex items-center justify-center text-xs"
                        >
                          -
                        </button>
                        <input
                          type="number"
                          min="0"
                          value={formLosses}
                          onChange={e => setFormLosses(Math.max(0, parseInt(e.target.value, 10) || 0))}
                          className="w-8 sm:w-10 bg-slate-950 border border-slate-800 rounded-md py-0.5 sm:py-1 text-center text-xs font-black text-rose-400 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => setFormLosses(prev => prev + 1)}
                          className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-rose-600/30 hover:bg-rose-600/50 text-rose-300 font-bold flex items-center justify-center text-xs"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* Empates */}
                    <div className="bg-slate-900 border border-amber-500/30 rounded-xl p-1.5 sm:p-2.5 text-center space-y-1 sm:space-y-1.5">
                      <span className="text-[10px] sm:text-[11px] font-bold text-amber-400 uppercase">Empates</span>
                      <div className="flex items-center justify-center space-x-1 sm:space-x-2">
                        <button
                          type="button"
                          onClick={() => setFormDraws(prev => Math.max(0, prev - 1))}
                          className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold flex items-center justify-center text-xs"
                        >
                          -
                        </button>
                        <input
                          type="number"
                          min="0"
                          value={formDraws}
                          onChange={e => setFormDraws(Math.max(0, parseInt(e.target.value, 10) || 0))}
                          className="w-8 sm:w-10 bg-slate-950 border border-slate-800 rounded-md py-0.5 sm:py-1 text-center text-xs font-black text-amber-400 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => setFormDraws(prev => prev + 1)}
                          className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-amber-600/30 hover:bg-amber-600/50 text-amber-300 font-bold flex items-center justify-center text-xs"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 4. Deck Utilizado (Opcional) */}
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-300 flex items-center space-x-1.5">
                      <i className="fas fa-layer-group text-purple-400"></i>
                      <span>Deck Utilizado (Opcional)</span>
                    </label>
                  </div>

                  {userDecksList.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                      <div>
                        <select
                          value={selectedDeckOption}
                          onChange={e => handleDeckOptionChange(e.target.value)}
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-purple-500"
                        >
                          <option value="manual">Digitar nome manualmente</option>
                          {userDecksList.map(d => (
                            <option key={`user-deck-${d.id}`} value={d.id}>
                              {d.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <input
                          type="text"
                          placeholder="Nome do deck (ex: Sabine, Enel...)"
                          value={formDeckName}
                          onChange={e => setFormDeckName(e.target.value)}
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                        />
                      </div>
                    </div>
                  ) : (
                    <input
                      type="text"
                      placeholder="Nome do deck (ex: Sabine Aggro, Enel Control)"
                      value={formDeckName}
                      onChange={e => setFormDeckName(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 sm:px-3.5 py-2 sm:py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                    />
                  )}
                </div>

                {/* 5. Colocação e Total de Jogadores (Opcionais) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-300 flex items-center space-x-1.5">
                      <i className="fas fa-medal text-amber-400"></i>
                      <span>Colocação Final (Posição)</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      placeholder="Ex: 1 (Campeão), 2 (Vice), 4, 8..."
                      value={formPlacement}
                      onChange={e => setFormPlacement(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 sm:px-3.5 py-2 sm:py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-300 flex items-center space-x-1.5">
                      <i className="fas fa-users text-blue-400"></i>
                      <span>Total de Jogadores</span>
                    </label>
                    <input
                      type="number"
                      min="2"
                      placeholder="Ex: 16, 24, 32, 64..."
                      value={formTotalPlayers}
                      onChange={e => setFormTotalPlayers(e.target.value)}
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 sm:px-3.5 py-2 sm:py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                    />
                  </div>
                </div>

                {/* Botões de Ação do Footer */}
                <div className="pt-3 sm:pt-4 border-t border-slate-800 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-0 sm:space-x-3">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="w-full sm:w-auto px-4 py-2.5 hover:bg-slate-800 text-slate-400 hover:text-white rounded-xl text-xs font-bold transition-colors text-center"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-lg shadow-purple-600/20 transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                        <span>Salvando...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check"></i>
                        <span>{editingReportId ? 'Salvar Alterações' : 'Salvar Report'}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal de Confirmação de Exclusão de Report */}
      <AnimatePresence>
        {reportToDelete && (
          <div
            id="delete-report-modal-backdrop"
            className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/85 backdrop-blur-sm"
            onClick={(e) => {
              if (e.target === e.currentTarget && !isDeleting) setReportToDelete(null);
            }}
          >
            <motion.div
              id="delete-report-modal-dialog"
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.15 }}
              className="relative w-full max-w-md bg-slate-900 border border-rose-500/30 rounded-2xl sm:rounded-3xl p-5 sm:p-6 shadow-2xl space-y-4 sm:space-y-5"
            >
              <div className="flex items-center space-x-3.5">
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-rose-500/20 border border-rose-500/30 flex items-center justify-center text-rose-400 shrink-0">
                  <i className="fas fa-trash-can text-lg sm:text-xl"></i>
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-black text-white uppercase tracking-tight">
                    Excluir Report de Torneio?
                  </h3>
                  <p className="text-xs text-slate-400 font-medium truncate">
                    Evento: <strong className="text-white">"{reportToDelete.name || 'Sem nome'}"</strong>
                  </p>
                </div>
              </div>

              <div className="text-xs text-slate-300 bg-slate-950/70 p-3.5 rounded-xl sm:rounded-2xl border border-slate-800 space-y-1.5 leading-relaxed">
                <p>
                  Esta ação excluirá permanentemente o histórico deste evento, incluindo placar ({reportToDelete.wins ?? 0}V - {reportToDelete.losses ?? 0}D{reportToDelete.draws ? ` - ${reportToDelete.draws}E` : ''}) e todas as rodadas registradas.
                </p>
                <p className="text-rose-400/90 font-semibold text-[11px]">
                  Esta operação não poderá ser desfeita.
                </p>
              </div>

              <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-0 sm:space-x-3 pt-1">
                <button
                  type="button"
                  id="cancel-delete-report-button"
                  disabled={isDeleting}
                  onClick={() => setReportToDelete(null)}
                  className="w-full sm:w-auto px-4 py-2.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 text-xs font-bold uppercase tracking-wider rounded-xl transition-all text-center"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  id="confirm-delete-report-button"
                  disabled={isDeleting}
                  onClick={handleConfirmDeleteReport}
                  className="w-full sm:w-auto px-5 py-2.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white text-xs font-black uppercase tracking-wider rounded-xl shadow-lg shadow-rose-600/30 transition-all flex items-center justify-center space-x-2 active:scale-95"
                >
                  {isDeleting ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                      <span>Excluindo...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash-can text-[11px]"></i>
                      <span>Sim, Excluir</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

